/**
 * applyAddFieldState — AK-334 «Tap-to-field».
 *
 * Чистый reducer prev→next для addFieldStore → императивные вызовы
 * AddFieldController. Вынесен из подписки в v2.tsx, чтобы покрыть юнит-тестом
 * РЕ-ВХОДИМОСТЬ (та же P0-грабля, что в measure/editor, ADR-038).
 *
 * ── Почему это критично:
 * side-effect'ы контроллера могут СИНХРОННО дёрнуть стор (например beginDetect
 * из tap-обработчика или failDetect по мгновенной ошибке mock), что снова
 * триггерит подписку → повторный applyAddFieldState(). Если prev-снапшот
 * обновить ПОСЛЕ side-effect'ов, вложенный вызов увидит старую фазу → повторит
 * тот же side-effect → рекурсия.
 *
 * Решение (как в applyMeasureState): захватываем старый prev в локали, СРАЗУ
 * перезаписываем prev новым next, и ТОЛЬКО ПОТОМ дёргаем side-effect'ы — вложенный
 * apply увидит prev == next → no-op.
 */
import type { AddFieldPhase, Point } from './addFieldStore';
import type { DelineateGeometry } from './delineateApi';

/** Срез стора, который читает reducer. */
export interface AddFieldSnapshot {
  active: boolean;
  phase: AddFieldPhase;
  tapPoint: Point | null;
  draftGeometry: DelineateGeometry | null;
  editSignal: number;
}

/** Мутируемый prev-снапшот (передаётся по ссылке между вызовами reducer'а). */
export interface AddFieldPrevRef {
  active: boolean;
  phase: AddFieldPhase;
  tapPoint: Point | null;
  draftGeometry: DelineateGeometry | null;
  editSignal: number;
}

/** Императивная поверхность контроллера, которую дёргает reducer. */
export interface AddFieldControllerLike {
  /** Включить режим: курсор-прицел, слушатель тапа. */
  activate(): void;
  /** Выключить режим: снять слушатели, убрать оверлей, вернуть курсор. */
  deactivate(): void;
  /** Показать пульс-лоадер на точке тапа (фаза detecting). */
  showDetecting(point: Point): void;
  /** Нарисовать amber-пунктирный черновик (фаза draft/form). */
  showDraft(geometry: DelineateGeometry): void;
  /** Убрать оверлей черновика и пульс (armed/reset/editing). */
  clearOverlay(): void;
}

export function initAddFieldPrev(init: AddFieldSnapshot): AddFieldPrevRef {
  return {
    active: false,
    phase: 'idle',
    tapPoint: null,
    draftGeometry: null,
    editSignal: init.editSignal,
  };
}

/**
 * Применить переход prev→next к контроллеру.
 *
 * ВАЖЕН ПОРЯДОК: сначала захватываем старый prev в локали, затем СРАЗУ
 * перезаписываем prev новым next, и ТОЛЬКО ПОТОМ дёргаем side-effect'ы.
 */
export function applyAddFieldState(
  prev: AddFieldPrevRef,
  next: AddFieldSnapshot,
  ctrl: AddFieldControllerLike,
): void {
  const pActive = prev.active;
  const pPhase = prev.phase;

  // Снимок ДО side-effect'ов: re-entrant apply увидит уже актуальные prev*.
  prev.active = next.active;
  prev.phase = next.phase;
  prev.tapPoint = next.tapPoint;
  prev.draftGeometry = next.draftGeometry;
  prev.editSignal = next.editSignal;

  // 1. Вкл/выкл режима.
  if (next.active && !pActive) {
    ctrl.activate();
  } else if (!next.active && pActive) {
    ctrl.deactivate();
    return; // после deactivate оверлей уже снят — фазовые эффекты не нужны.
  }
  if (!next.active) return;

  // 2. Эффекты по смене фазы. Реагируем только на ПЕРЕХОД (phase !== pPhase),
  //    чтобы не перерисовывать оверлей на каждый сторонний set (тосты и т.п.).
  if (next.phase === pPhase) return;

  switch (next.phase) {
    case 'detecting':
      if (next.tapPoint) ctrl.showDetecting(next.tapPoint);
      break;
    case 'draft':
    case 'form':
      // form приходит из draft (или из editing после «Готово») — держим
      // amber-черновик под панелью/формой.
      if (next.draftGeometry) ctrl.showDraft(next.draftGeometry);
      break;
    case 'editing':
    case 'drawing':
      // Черновик уходит в РЕДАКТОР (own terra-draw слой): editing = правка
      // существующего контура, drawing = поточечное рисование с нуля (AK-373).
      // Наш amber-оверлей убираем, чтобы не дублировать контур редактора.
      ctrl.clearOverlay();
      break;
    case 'armed':
      // Вернулись в режим ожидания (activate / reset / после сохранения) —
      // чистим пульс и черновик.
      ctrl.clearOverlay();
      break;
    default:
      break;
  }
}
